import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, FileSpreadsheet, Users, MapPin, Calendar, Filter, AlertCircle, BarChart3, TrendingUp, Award, ChevronUp, ChevronDown, Brain, Shield, Mail, Save, Loader2, Settings, ArrowLeftRight, AlertTriangle, ScanLine } from 'lucide-react';
import { supabase } from '../supabase';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import type { VergeCondition, AIModel } from '../types';
import { useAuth } from '../hooks/useAuth';
import { AuthorityManagement } from './AuthorityManagement';
import { EmailAutomation } from './EmailAutomation';

type SortDirection = 'asc' | 'desc' | null;
type ReportsSortField = 'grade' | 'street_name' | 'local_authority' | 'timestamp';
type UsersSortField = 'rank' | 'device_id' | 'report_count' | 'first_report' | 'last_report' | 'days_active';

interface UserStats {
  device_id: string;
  report_count: number;
  first_report: string;
  last_report: string;
  grade_breakdown: {
    A: number;
    B: number;
    C: number;
    D: number;
  };
}

interface AdminDashboardProps {
  onBack: () => void;
}

export function AdminDashboard({ onBack }: AdminDashboardProps) {
  const { profile, updateAIModel } = useAuth();
  const [reports, setReports] = useState<VergeCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedAuthority, setSelectedAuthority] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [authorities, setAuthorities] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<'reports' | 'users' | 'authorities' | 'emails' | 'settings'>('reports');
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [loadingUserStats, setLoadingUserStats] = useState(false);
  const [reportsSortField, setReportsSortField] = useState<ReportsSortField | null>(null);
  const [reportsSortDirection, setReportsSortDirection] = useState<SortDirection>(null);
  const [usersSortField, setUsersSortField] = useState<UsersSortField | null>(null);
  const [usersSortDirection, setUsersSortDirection] = useState<SortDirection>(null);
  const [userSearchTerm, setUserSearchTerm] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingAIModel, setUpdatingAIModel] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderEmailOriginal, setSenderEmailOriginal] = useState('');
  const [savingSenderEmail, setSavingSenderEmail] = useState(false);
  const [senderEmailSuccess, setSenderEmailSuccess] = useState(false);

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    fetchReports();
    fetchAuthorities();
    if (activeTab === 'users') {
      fetchUserStats();
    }
    if (activeTab === 'settings') {
      fetchSenderEmail();
    }
  }, [selectedGrade, selectedAuthority, startDate, endDate, currentPage, activeTab]);

  const fetchSenderEmail = async () => {
    const { data } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'sender_email')
      .maybeSingle();
    if (data) {
      setSenderEmail(data.value);
      setSenderEmailOriginal(data.value);
    }
  };

  const saveSenderEmail = async () => {
    setSavingSenderEmail(true);
    setSenderEmailSuccess(false);
    const { error } = await supabase
      .from('app_settings')
      .upsert({
        key: 'sender_email',
        value: senderEmail,
        updated_at: new Date().toISOString(),
        updated_by: profile?.id
      });
    if (error) {
      alert('Failed to save sender email setting');
    } else {
      setSenderEmailOriginal(senderEmail);
      setSenderEmailSuccess(true);
      setTimeout(() => setSenderEmailSuccess(false), 3000);
    }
    setSavingSenderEmail(false);
  };

  const fetchUserStats = async () => {
    console.log('📊 Fetching user statistics...');
    setLoadingUserStats(true);
    
    try {
      console.log('🔍 Starting fetchUserStats...');
      
      // Fetch all reports to calculate user stats
      console.log('📊 Fetching all reports from Supabase...');
      const { data: allReports, error } = await supabase
        .from('verge_conditions')
        .select('device_id, grade, timestamp')
        .not('device_id', 'is', null)
        .order('timestamp', { ascending: true });

      if (error) {
        console.error('❌ Supabase query error:', error);
        throw error;
      }

      console.log('✅ Raw reports from Supabase:', {
        totalCount: allReports?.length || 0,
        firstFew: allReports?.slice(0, 3),
        deviceIds: [...new Set(allReports?.map(r => r.device_id).filter(Boolean))].slice(0, 10)
      });

      // Check specifically for our target device
      const targetReports = allReports?.filter(r => r.device_id === '9ab0e083a6e6d728c5d1d90505bb23d5') || [];
      console.log('🎯 Target device reports:', {
        count: targetReports.length,
        reports: targetReports.map(r => ({
          timestamp: r.timestamp,
          grade: r.grade,
          street_name: r.street_name
        }))
      });

      // 🔍 DEBUG: Log the date range of fetched reports
      if (allReports && allReports.length > 0) {
        const firstReport = allReports[0];
        const lastReport = allReports[allReports.length - 1];
        console.log('🔍 DEBUG: Fetched reports date range:');
        console.log(`   📅 First report: ${firstReport.timestamp} (${new Date(firstReport.timestamp).toLocaleString()})`);
        console.log(`   📅 Last report: ${lastReport.timestamp} (${new Date(lastReport.timestamp).toLocaleString()})`);
        console.log(`   📊 Total reports fetched: ${allReports.length}`);
        
        // 🔍 DEBUG: Check for January 2025 reports
        const january2025Reports = allReports.filter(report => {
          const reportDate = new Date(report.timestamp);
          return reportDate.getFullYear() === 2025 && reportDate.getMonth() === 0; // January is month 0
        });
        console.log(`   📅 January 2025 reports found: ${january2025Reports.length}`);
        
        if (january2025Reports.length > 0) {
          console.log(`   📅 Latest January 2025 report: ${january2025Reports[january2025Reports.length - 1].timestamp}`);
        }
        
        // 🔍 DEBUG: Show last 10 reports by timestamp
        const last10Reports = allReports.slice(-10);
        console.log('🔍 DEBUG: Last 10 reports by timestamp:');
        last10Reports.forEach((report, index) => {
          console.log(`   ${index + 1}. ${report.timestamp} (${new Date(report.timestamp).toLocaleString()})`);
        });
      }
      console.log(`📈 Processing ${allReports?.length || 0} reports for user stats...`);

      // Group reports by device_id and calculate stats
      const statsMap = new Map<string, UserStats>();

      allReports?.forEach(report => {
        const deviceId = report.device_id!;
        
        // 🔍 DEBUG: Check for the specific device ID you mentioned
        if (deviceId === '9ab0e083a6e6d728c5d1d90505bb23d5') {
          console.log(`🎯 FOUND TARGET DEVICE: ${deviceId}`);
          console.log(`   📅 Report timestamp: ${report.timestamp} (${new Date(report.timestamp).toLocaleString()})`);
          console.log(`   📊 Report grade: ${report.grade}`);
        }
        
        if (!statsMap.has(deviceId)) {
          statsMap.set(deviceId, {
            device_id: deviceId,
            report_count: 0,
            first_report: report.timestamp,
            last_report: report.timestamp,
            grade_breakdown: { A: 0, B: 0, C: 0, D: 0 }
          });
        }

        const stats = statsMap.get(deviceId)!;
        stats.report_count++;
        stats.grade_breakdown[report.grade as keyof typeof stats.grade_breakdown]++;
        
        // Update first/last report dates
        if (new Date(report.timestamp) < new Date(stats.first_report)) {
          stats.first_report = report.timestamp;
        }
        if (new Date(report.timestamp) > new Date(stats.last_report)) {
          // 🔍 DEBUG: Log when last_report is updated
          console.log(`🔍 DEBUG: Updating last_report for ${deviceId.substring(0, 8)}...`);
          console.log(`   📅 Old last_report: ${stats.last_report} (${new Date(stats.last_report).toLocaleString()})`);
          console.log(`   📅 New timestamp: ${report.timestamp} (${new Date(report.timestamp).toLocaleString()})`);
          stats.last_report = report.timestamp;
        }
      });

      // Convert to array and sort by report count
      const sortedStats = Array.from(statsMap.values())
        .sort((a, b) => b.report_count - a.report_count);

      // 🔍 DEBUG: Log final last_report values for top users
      console.log('🔍 DEBUG: Final last_report values for top 5 users:');
      sortedStats.slice(0, 5).forEach((user, index) => {
        console.log(`   #${index + 1} ${user.device_id.substring(0, 8)}... - Last: ${user.last_report} (${new Date(user.last_report).toLocaleString()})`);
      });
      console.log(`✅ User stats calculated for ${sortedStats.length} users`);
      
      // 🔍 DEBUG: Check if the target device made it to final stats
      const targetUser = sortedStats.find(user => user.device_id === '9ab0e083a6e6d728c5d1d90505bb23d5');
      if (targetUser) {
        console.log(`🎯 TARGET DEVICE IN FINAL STATS:`);
        console.log(`   📊 Report count: ${targetUser.report_count}`);
        console.log(`   📅 Last report: ${targetUser.last_report} (${new Date(targetUser.last_report).toLocaleString()})`);
      } else {
        console.log(`❌ TARGET DEVICE NOT FOUND IN FINAL STATS`);
      }
      
      setUserStats(sortedStats);
    } catch (error) {
      console.error('❌ Error fetching user stats:', error);
      setUserStats([]); // Set empty array on error
    } finally {
      setLoadingUserStats(false);
    }
  };

  const fetchAuthorities = async () => {
    try {
      const { data, error } = await supabase
        .from('verge_conditions')
        .select('local_authority')
        .not('local_authority', 'is', null)
        .not('local_authority', 'eq', '')
        .not('local_authority', 'eq', 'Unknown authority');

      if (error) throw error;

      const uniqueAuthorities = [...new Set(data.map(item => item.local_authority))]
        .filter(Boolean)
        .sort();
      
      setAuthorities(uniqueAuthorities);
    } catch (error) {
      console.error('Error fetching authorities:', error);
    }
  };

  // Filter users based on search term
  const filteredUsers = searchTerm 
    ? userStats.filter(user => 
        user.device_id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : userStats;

  const fetchReports = async () => {
    console.log('📊 Fetching reports with filters:', {
      selectedGrade,
      selectedAuthority,
      startDate,
      endDate,
      currentPage
    });
    
    try {
      setLoading(true);
      
      let query = supabase
        .from('verge_conditions')
        .select('*', { count: 'exact' })
        .order('timestamp', { ascending: false });

      // Apply filters
      if (selectedGrade) {
        console.log(`🔍 Applying grade filter: ${selectedGrade}`);
        query = query.eq('grade', selectedGrade);
      }
      if (selectedAuthority) {
        console.log(`🔍 Applying authority filter: ${selectedAuthority}`);
        query = query.eq('local_authority', selectedAuthority);
      }
      if (startDate) {
        console.log(`🔍 Applying start date filter: ${startDate}`);
        query = query.gte('timestamp', startDate);
      }
      if (endDate) {
        console.log(`🔍 Applying end date filter: ${endDate}`);
        query = query.lte('timestamp', endDate + 'T23:59:59.999Z');
      }

      // Apply pagination
      const from = currentPage * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      console.log(`📄 Applying pagination: ${from} to ${to}`);
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      console.log(`✅ Reports fetched: ${data?.length || 0} records, total count: ${count}`);
      setReports(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFilteredReportsForExport = async (): Promise<VergeCondition[]> => {
    console.log('🚀 Starting export - fetching ALL filtered reports...');
    console.log('📊 Current filters:', {
      selectedGrade,
      selectedAuthority,
      startDate,
      endDate
    });

    try {
      let allReports: VergeCondition[] = [];
      let hasMore = true;
      let offset = 0;
      const batchSize = 1000;
      let batchCount = 0;

      while (hasMore && batchCount < 1000) { // Safety limit
        console.log(`📦 Fetching batch ${batchCount + 1} (offset: ${offset})...`);
        
        let query = supabase
          .from('verge_conditions')
          .select('*')
          .order('timestamp', { ascending: false });

        // Apply the same filters as the display
        if (selectedGrade) {
          console.log(`🔍 Applying grade filter: ${selectedGrade}`);
          query = query.eq('grade', selectedGrade);
        }
        if (selectedAuthority) {
          console.log(`🔍 Applying authority filter: ${selectedAuthority}`);
          query = query.eq('local_authority', selectedAuthority);
        }
        if (startDate) {
          console.log(`🔍 Applying start date filter: ${startDate}`);
          query = query.gte('timestamp', startDate);
        }
        if (endDate) {
          console.log(`🔍 Applying end date filter: ${endDate}`);
          query = query.lte('timestamp', endDate + 'T23:59:59.999Z');
        }

        // Apply pagination for this batch
        query = query.range(offset, offset + batchSize - 1);

        const { data, error } = await query;

        if (error) {
          console.error('❌ Error fetching batch:', error);
          throw error;
        }

        console.log(`✅ Batch ${batchCount + 1} returned ${data?.length || 0} records`);

        if (data && data.length > 0) {
          allReports = [...allReports, ...data];
          offset += batchSize;
          batchCount++;
          
          // If we got less than the batch size, we've reached the end
          if (data.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      console.log(`🎉 Export fetch complete! Total records: ${allReports.length}`);
      return allReports;
    } catch (error) {
      console.error('❌ Error in fetchAllFilteredReportsForExport:', error);
      throw error;
    }
  };

  const exportToExcel = async () => {
    console.log('📊 Excel export button clicked!');
    setExporting(true);
    
    try {
      console.log('🔄 Fetching all filtered reports for Excel export...');
      const allReports = await fetchAllFilteredReportsForExport();
      
      console.log(`📋 Preparing Excel file with ${allReports.length} records...`);
      
      const exportData = allReports.map(report => ({
        'ID': report.id,
        'Grade': report.grade,
        'Street Name': report.street_name || '',
        'Local Authority': report.local_authority || '',
        'Latitude': report.latitude,
        'Longitude': report.longitude,
        'Timestamp': new Date(report.timestamp).toLocaleString(),
        'Device ID': report.device_id || '',
        'Photo URL': report.photo_url || '',
        'Litter Left Side': report.litter_left_side ? 'Yes' : 'No',
        'Litter Right Side': report.litter_right_side ? 'Yes' : 'No',
        'Litter Central Reservation': report.litter_central_reservation ? 'Yes' : 'No',
        'Dangerous Debris': report.dangerous_debris ? 'Yes' : 'No',
        'Registration Plate Detected': report.registration_plate_detected ? 'Yes' : 'No',
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Verge Conditions');

      const fileName = `verge-conditions-${new Date().toISOString().split('T')[0]}.xlsx`;
      console.log(`💾 Saving Excel file: ${fileName}`);
      
      XLSX.writeFile(workbook, fileName);
      console.log('✅ Excel export completed successfully!');
    } catch (error) {
      console.error('❌ Excel export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    console.log('📊 CSV export button clicked!');
    setExporting(true);
    
    try {
      console.log('🔄 Fetching all filtered reports for CSV export...');
      const allReports = await fetchAllFilteredReportsForExport();
      
      console.log(`📋 Preparing CSV file with ${allReports.length} records...`);
      
      const exportData = allReports.map(report => ({
        'ID': report.id,
        'Grade': report.grade,
        'Street Name': report.street_name || '',
        'Local Authority': report.local_authority || '',
        'Latitude': report.latitude,
        'Longitude': report.longitude,
        'Timestamp': new Date(report.timestamp).toLocaleString(),
        'Device ID': report.device_id || '',
        'Photo URL': report.photo_url || '',
        'Litter Left Side': report.litter_left_side ? 'Yes' : 'No',
        'Litter Right Side': report.litter_right_side ? 'Yes' : 'No',
        'Litter Central Reservation': report.litter_central_reservation ? 'Yes' : 'No',
        'Dangerous Debris': report.dangerous_debris ? 'Yes' : 'No',
        'Registration Plate Detected': report.registration_plate_detected ? 'Yes' : 'No',
      }));

      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `verge-conditions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ CSV export completed successfully!');
    } catch (error) {
      console.error('❌ CSV export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const exportUsersToCSV = async () => {
    console.log('📊 Users CSV export button clicked!');
    setExporting(true);
    
    try {
      console.log('🔄 Fetching user stats for CSV export...');
      
      // Use existing user stats or fetch fresh data
      let statsToExport = userStats;
      if (statsToExport.length === 0) {
        console.log('📈 No cached user stats, fetching fresh data...');
        await fetchUserStats();
        statsToExport = userStats;
      }
      
      console.log(`📋 Preparing CSV file with ${statsToExport.length} users...`);
      
      const exportData = statsToExport.map((user, index) => ({
        'Rank': index + 1,
        'Device ID': user.device_id,
        'Total Reports': user.report_count,
        'Grade A': user.grade_breakdown.A,
        'Grade B': user.grade_breakdown.B,
        'Grade C': user.grade_breakdown.C,
        'Grade D': user.grade_breakdown.D,
        'First Report': new Date(user.first_report).toLocaleString(),
        'Last Report': new Date(user.last_report).toLocaleString(),
        'Days Active': Math.ceil((new Date(user.last_report).getTime() - new Date(user.first_report).getTime()) / (1000 * 60 * 60 * 24)) + 1
      }));

      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `user-stats-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Users CSV export completed successfully!');
    } catch (error) {
      console.error('❌ Users CSV export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const exportUsersToExcel = async () => {
    console.log('📊 Users Excel export button clicked!');
    setExporting(true);
    
    try {
      console.log('🔄 Fetching user stats for Excel export...');
      
      // Use existing user stats or fetch fresh data
      let statsToExport = userStats;
      if (statsToExport.length === 0) {
        console.log('📈 No cached user stats, fetching fresh data...');
        await fetchUserStats();
        statsToExport = userStats;
      }
      
      console.log(`📋 Preparing Excel file with ${statsToExport.length} users...`);
      
      const exportData = statsToExport.map((user, index) => ({
        'Rank': index + 1,
        'Device ID': user.device_id,
        'Total Reports': user.report_count,
        'Grade A': user.grade_breakdown.A,
        'Grade B': user.grade_breakdown.B,
        'Grade C': user.grade_breakdown.C,
        'Grade D': user.grade_breakdown.D,
        'First Report': new Date(user.first_report).toLocaleString(),
        'Last Report': new Date(user.last_report).toLocaleString(),
        'Days Active': Math.ceil((new Date(user.last_report).getTime() - new Date(user.first_report).getTime()) / (1000 * 60 * 60 * 24)) + 1
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'User Statistics');

      const fileName = `user-stats-${new Date().toISOString().split('T')[0]}.xlsx`;
      console.log(`💾 Saving Excel file: ${fileName}`);
      
      XLSX.writeFile(workbook, fileName);
      console.log('✅ Users Excel export completed successfully!');
    } catch (error) {
      console.error('❌ Users Excel export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleReportsSort = (field: ReportsSortField) => {
    if (reportsSortField === field) {
      // Cycle through: asc -> desc -> null
      if (reportsSortDirection === 'asc') {
        setReportsSortDirection('desc');
      } else if (reportsSortDirection === 'desc') {
        setReportsSortDirection(null);
        setReportsSortField(null);
      }
    } else {
      setReportsSortField(field);
      setReportsSortDirection('asc');
    }
    setCurrentPage(0); // Reset to first page when sorting
  };

  const handleUsersSort = (field: UsersSortField) => {
    if (usersSortField === field) {
      // Cycle through: asc -> desc -> null
      if (usersSortDirection === 'asc') {
        setUsersSortDirection('desc');
      } else if (usersSortDirection === 'desc') {
        setUsersSortDirection(null);
        setUsersSortField(null);
      }
    } else {
      setUsersSortField(field);
      setUsersSortDirection('asc');
    }
  };

  const getSortIcon = (field: string, currentField: string | null, direction: SortDirection) => {
    if (field !== currentField || direction === null) {
      return <ChevronUp className="w-4 h-4 text-gray-300" />;
    }
    return direction === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-blue-500" /> : 
      <ChevronDown className="w-4 h-4 text-blue-500" />;
  };

  // Sort reports data
  const sortedReports = React.useMemo(() => {
    if (!reportsSortField || !reportsSortDirection) {
      return reports;
    }

    return [...reports].sort((a, b) => {
      let aValue: any = a[reportsSortField];
      let bValue: any = b[reportsSortField];

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convert to strings for comparison (except for timestamp)
      if (reportsSortField === 'timestamp') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) {
        return reportsSortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return reportsSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [reports, reportsSortField, reportsSortDirection]);

  // Sort users data
  const sortedUserStats = React.useMemo(() => {
    if (!usersSortField || !usersSortDirection) {
      // Apply search filter
      const filtered = userStats.filter(user => 
        userSearchTerm === '' || 
        user.device_id.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
      return filtered;
    }

    // Apply search filter first, then sort
    const filtered = userStats.filter(user => 
      userSearchTerm === '' || 
      user.device_id.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
    
    return [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (usersSortField) {
        case 'rank':
          aValue = userStats.indexOf(a) + 1;
          bValue = userStats.indexOf(b) + 1;
          break;
        case 'device_id':
          aValue = a.device_id.toLowerCase();
          bValue = b.device_id.toLowerCase();
          break;
        case 'report_count':
          aValue = a.report_count;
          bValue = b.report_count;
          break;
        case 'first_report':
          aValue = new Date(a.first_report).getTime();
          bValue = new Date(b.first_report).getTime();
          break;
        case 'last_report':
          aValue = new Date(a.last_report).getTime();
          bValue = new Date(b.last_report).getTime();
          break;
        case 'days_active':
          aValue = Math.ceil((new Date(a.last_report).getTime() - new Date(a.first_report).getTime()) / (1000 * 60 * 60 * 24)) + 1;
          bValue = Math.ceil((new Date(b.last_report).getTime() - new Date(b.first_report).getTime()) / (1000 * 60 * 60 * 24)) + 1;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return usersSortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return usersSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [userStats, usersSortField, usersSortDirection]);

  const clearFilters = () => {
    setSelectedGrade('');
    setSelectedAuthority('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(0);
  };

  const handleAIModelChange = async (model: AIModel) => {
    setUpdatingAIModel(true);
    const { error } = await updateAIModel(model);
    if (error) {
      alert('Failed to update AI model preference');
    }
    setUpdatingAIModel(false);
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
          <div className="w-16" />
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('reports')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'reports'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Reports Dashboard
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'users'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              User Statistics
            </button>
            <button
              onClick={() => setActiveTab('authorities')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'authorities'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Authorities
            </button>
            <button
              onClick={() => setActiveTab('emails')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'emails'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Email Chasers
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'settings'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Brain className="w-4 h-4 inline mr-2" />
              AI Settings
            </button>
          </div>
        </div>

        {activeTab === 'reports' ? (
          <>
            {/* Reports Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <BarChart3 className="w-8 h-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Reports</p>
                    <p className="text-2xl font-bold text-gray-900">{totalCount.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <MapPin className="w-8 h-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Authorities</p>
                    <p className="text-2xl font-bold text-gray-900">{authorities.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Calendar className="w-8 h-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Filtered Results</p>
                    <p className="text-2xl font-bold text-gray-900">{totalCount.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Filter className="w-5 h-5 text-gray-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                  <select
                    value={selectedGrade}
                    onChange={(e) => {
                      setSelectedGrade(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Grades</option>
                    <option value="A">Grade A</option>
                    <option value="B">Grade B</option>
                    <option value="C">Grade C</option>
                    <option value="D">Grade D</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Authority</label>
                  <select
                    value={selectedAuthority}
                    onChange={(e) => {
                      setSelectedAuthority(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Authorities</option>
                    {authorities.map(authority => (
                      <option key={authority} value={authority}>{authority}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Download className="w-5 h-5 text-gray-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-800">Export Reports Data</h2>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={exportToCSV}
                  disabled={exporting}
                  className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'CSV'}
                </button>
                
                <button
                  onClick={exportToExcel}
                  disabled={exporting}
                  className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Excel'}
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mt-2">
                Export will include all {totalCount.toLocaleString()} filtered records, not just the current page.
              </p>
            </div>

            {/* Reports Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">
                  Reports ({totalCount.toLocaleString()} total)
                </h2>
              </div>
              
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading reports...</p>
                </div>
              ) : reports.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No reports found matching the current filters.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleReportsSort('grade')}
                          >
                            <div className="flex items-center gap-1">
                              Grade
                              {getSortIcon('grade', reportsSortField, reportsSortDirection)}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleReportsSort('street_name')}
                          >
                            <div className="flex items-center gap-1">
                              Street
                              {getSortIcon('street_name', reportsSortField, reportsSortDirection)}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleReportsSort('local_authority')}
                          >
                            <div className="flex items-center gap-1">
                              Authority
                              {getSortIcon('local_authority', reportsSortField, reportsSortDirection)}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleReportsSort('timestamp')}
                          >
                            <div className="flex items-center gap-1">
                              Date
                              {getSortIcon('timestamp', reportsSortField, reportsSortDirection)}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AI Flags</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sortedReports.map((report) => (
                          <tr key={report.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                report.grade === 'A' ? 'bg-green-100 text-green-800' :
                                report.grade === 'B' ? 'bg-yellow-100 text-yellow-800' :
                                report.grade === 'C' ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                Grade {report.grade}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {report.street_name || 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {report.local_authority || 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(report.timestamp).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {report.latitude.toFixed(4)}, {report.longitude.toFixed(4)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                {report.litter_left_side && (
                                  <span title="Litter: left side" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 font-medium">
                                    <ArrowLeftRight className="w-3 h-3" />L
                                  </span>
                                )}
                                {report.litter_right_side && (
                                  <span title="Litter: right side" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 font-medium">
                                    <ArrowLeftRight className="w-3 h-3" />R
                                  </span>
                                )}
                                {report.litter_central_reservation && (
                                  <span title="Litter: central reservation" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 font-medium">
                                    <ArrowLeftRight className="w-3 h-3" />C
                                  </span>
                                )}
                                {report.dangerous_debris && (
                                  <span title="Dangerous debris detected" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">
                                    <AlertTriangle className="w-3 h-3" />
                                  </span>
                                )}
                                {report.registration_plate_detected && (
                                  <span title="Registration plate detected" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 font-medium">
                                    <ScanLine className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {currentPage * ITEMS_PER_PAGE + 1} to {Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalCount)} of {totalCount} results
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                          disabled={currentPage === 0}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                          Page {currentPage + 1} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                          disabled={currentPage >= totalPages - 1}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : activeTab === 'users' ? (
          <>
            {/* User Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Users</p>
                    <p className="text-2xl font-bold text-gray-900">{userStats.length.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <TrendingUp className="w-8 h-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Avg Reports/User</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {userStats.length > 0 ? Math.round(userStats.reduce((sum, user) => sum + user.report_count, 0) / userStats.length) : 0}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Award className="w-8 h-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Top User Reports</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {userStats.length > 0 ? userStats[0]?.report_count.toLocaleString() : 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* User Export Buttons */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Download className="w-5 h-5 text-gray-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-800">Export User Statistics</h2>
              </div>
              
              {/* User Search */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Users</label>
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder="Search by Device ID..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Try searching for: 9ab0e083a6e6d728c5d1d90505bb23d5
                </p>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={exportUsersToCSV}
                  disabled={exporting}
                  className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'CSV'}
                </button>
                
                <button
                  onClick={exportUsersToExcel}
                  disabled={exporting}
                  className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Excel'}
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mt-2">
                Export will include statistics for all {userStats.length.toLocaleString()} users.
              </p>
            </div>

            {/* User Statistics Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">
                  User Statistics ({userStats.length.toLocaleString()} users)
                </h2>
              </div>
              
              {loadingUserStats ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading user statistics...</p>
                </div>
              ) : userStats.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No user statistics available.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleUsersSort('rank')}
                        >
                          <div className="flex items-center gap-1">
                            Rank
                            {getSortIcon('rank', usersSortField, usersSortDirection)}
                          </div>
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleUsersSort('device_id')}
                        >
                          <div className="flex items-center gap-1">
                            Device ID
                            {getSortIcon('device_id', usersSortField, usersSortDirection)}
                          </div>
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleUsersSort('report_count')}
                        >
                          <div className="flex items-center gap-1">
                            Total Reports
                            {getSortIcon('report_count', usersSortField, usersSortDirection)}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade Breakdown</th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleUsersSort('first_report')}
                        >
                          <div className="flex items-center gap-1">
                            First Report
                            {getSortIcon('first_report', usersSortField, usersSortDirection)}
                          </div>
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleUsersSort('last_report')}
                        >
                          <div className="flex items-center gap-1">
                            Last Report
                            {getSortIcon('last_report', usersSortField, usersSortDirection)}
                          </div>
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleUsersSort('days_active')}
                        >
                          <div className="flex items-center gap-1">
                            Days Active
                            {getSortIcon('days_active', usersSortField, usersSortDirection)}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedUserStats.slice(0, 100).map((user, index) => {
                        const originalIndex = userStats.indexOf(user);
                        return (
                        <tr key={user.device_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              originalIndex === 0 ? 'bg-yellow-100 text-yellow-800' :
                              originalIndex === 1 ? 'bg-gray-100 text-gray-800' :
                              originalIndex === 2 ? 'bg-orange-100 text-orange-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              #{originalIndex + 1}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {user.device_id.substring(0, 12)}...
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                            {user.report_count.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex gap-1">
                              <span className="bg-green-100 text-green-800 px-1 rounded text-xs">A:{user.grade_breakdown.A}</span>
                              <span className="bg-yellow-100 text-yellow-800 px-1 rounded text-xs">B:{user.grade_breakdown.B}</span>
                              <span className="bg-orange-100 text-orange-800 px-1 rounded text-xs">C:{user.grade_breakdown.C}</span>
                              <span className="bg-red-100 text-red-800 px-1 rounded text-xs">D:{user.grade_breakdown.D}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.first_report).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.last_report).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {Math.ceil((new Date(user.last_report).getTime() - new Date(user.first_report).getTime()) / (1000 * 60 * 60 * 24)) + 1}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                  {userStats.length > 100 && (
                    <div className="px-6 py-4 border-t border-gray-200 text-center text-sm text-gray-500">
                      Showing top 100 of {sortedUserStats.length.toLocaleString()} filtered users. Export to see all {userStats.length.toLocaleString()} users.
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : activeTab === 'authorities' ? (
          <AuthorityManagement />
        ) : activeTab === 'emails' ? (
          <EmailAutomation />
        ) : activeTab === 'settings' ? (
          <>
            {/* AI Settings Tab */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2">AI Model Configuration</h2>
                <p className="text-gray-600 text-sm">
                  Choose which AI model to use for analyzing photos in AI Photo mode and Dashcam mode.
                </p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* OpenAI Option */}
                  <button
                    onClick={() => handleAIModelChange('openai')}
                    disabled={updatingAIModel || profile?.ai_model === 'openai'}
                    className={`p-6 rounded-lg border-2 transition-all ${
                      profile?.ai_model === 'openai'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-300 bg-white'
                    } ${updatingAIModel ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Brain className="w-6 h-6 text-blue-600" />
                        <h3 className="text-lg font-semibold text-gray-800">OpenAI</h3>
                      </div>
                      {profile?.ai_model === 'openai' && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 text-left">
                      Uses GPT-4 Vision for high-accuracy photo analysis. Cloud-based processing with reliable results.
                    </p>
                  </button>

                  {/* Ollama Option */}
                  <button
                    onClick={() => handleAIModelChange('ollama')}
                    disabled={updatingAIModel || profile?.ai_model === 'ollama'}
                    className={`p-6 rounded-lg border-2 transition-all ${
                      profile?.ai_model === 'ollama'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 hover:border-green-300 bg-white'
                    } ${updatingAIModel ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Brain className="w-6 h-6 text-green-600" />
                        <h3 className="text-lg font-semibold text-gray-800">Ollama</h3>
                      </div>
                      {profile?.ai_model === 'ollama' && (
                        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 text-left">
                      Uses Llama 3.2 Vision for local or self-hosted AI analysis. Privacy-focused with customizable deployment.
                    </p>
                  </button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Current Selection: {profile?.ai_model === 'ollama' ? 'Ollama (Llama 3.2 Vision)' : 'OpenAI (GPT-4 Vision)'}</p>
                      <p>This setting affects all AI-powered photo analysis features including AI Photo mode, Photo Upload mode, and Dashcam mode.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Email Sender Settings
                </h2>
                <p className="text-gray-600 text-sm">
                  Configure the "From" address used for all outgoing emails (report notifications, chasers, welcome emails).
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sender Email Address
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="CleanUpBritain <noreply@cleanupbritain.org>"
                    />
                    <button
                      onClick={saveSenderEmail}
                      disabled={savingSenderEmail || senderEmail === senderEmailOriginal}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {savingSenderEmail ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Format: Display Name &lt;email@domain.com&gt; -- e.g. CleanUpBritain &lt;noreply@cleanupbritain.org&gt;
                  </p>
                  {senderEmailSuccess && (
                    <p className="text-sm text-green-600 mt-2 font-medium">Sender email saved successfully.</p>
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Resend Domain Verification Required</p>
                      <p>
                        To send from a custom domain (not onboarding@resend.dev), you must first verify your domain in the
                        <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1">Resend dashboard</a>.
                        Until verified, emails will only send from the Resend sandbox address.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}